import React, {useCallback, useMemo} from 'react';
import {StyleSheet, Text, TouchableOpacity, useWindowDimensions, View} from 'react-native';
import {FontAwesome5, Ionicons, MaterialIcons} from '@expo/vector-icons';
import {priceFormatter} from '../helpers';
import {useTranslation} from 'react-i18next';
import TimeAgo from 'react-native-timeago';
import {OnlineOrderContext} from '../helpers/OnlineOrderContext';
import {getOrderPairingStats} from '../helpers/orderPairing';
import {getOrderRisk} from '../helpers/orderRisk';
import {haptics} from '../helpers/haptics';

const OrderRow = ({order, onPress, dense = false}) => {
    const {resetOnlineOrder} = React.useContext(OnlineOrderContext);
    const {t} = useTranslation();
    const {width} = useWindowDimensions();
    const isCompact = width < 720;

    const cardToneStyle = useMemo(() => {
        if (order?.storno || order?.status === 'cancelled') {
            return styles.stornoBackground;
        }
        if (order?.status === 'closed') {
            return styles.closedBackground;
        }
        if (order?.preOrder) {
            return styles.preOrderBackground;
        }
        return styles.defaultBackground;
    }, [order?.preOrder, order?.status, order?.storno]);

    const convertPaymentType = useCallback((paymentType) => {
        switch (paymentType) {
            case 'cash':
                return t('cash');
            case 'creditCard':
                return t('credit_card');
            case 'voucher':
                return t('voucher');
            case 'vipBalance':
                return t('vip_balance');
            case 'employeeBalance':
                return t('employee_balance');
            case 'temporary':
                return t('temporary');
            default:
                return `${paymentType || ''}`.trim() || t('temporary');
        }
    }, [t]);

    const getOrderIcon = useCallback((source) => {
        switch (source) {
            case 'terminal':
                return <MaterialIcons name="point-of-sale" size={18} color="#4b5563" />;
            case 'phone':
                return <FontAwesome5 name="mobile-alt" size={16} color="#4b5563" />;
            case 'webshop':
                return <Ionicons name="earth-outline" size={18} color="#4b5563" />;
            case 'wolt':
                return <MaterialIcons name="delivery-dining" size={18} color="#00C2E8" />;
            case 'netpincer':
                return <MaterialIcons name="delivery-dining" size={18} color="#FF6900" />;
            default:
                return <MaterialIcons name="point-of-sale" size={18} color="#4b5563" />;
        }
    }, []);

    const getOrderTypeIcon = useCallback((type) => {
        switch (type) {
            case 'takeAway':
                return <MaterialIcons name="takeout-dining" size={16} color="#4b5563" />;
            case 'dineIn':
                return <MaterialIcons name="restaurant" size={16} color="#4b5563" />;
            case 'online':
                return <MaterialIcons name="devices" size={16} color="#4b5563" />;
            case 'delivery':
                return <MaterialIcons name="delivery-dining" size={16} color="#4b5563" />;
            default:
                return <MaterialIcons name="takeout-dining" size={16} color="#4b5563" />;
        }
    }, []);

    const getStatusIcon = useCallback((status) => {
        switch (status) {
            case 'accepted':
                return <MaterialIcons name="check-circle" size={16} color="#16a34a" />;
            case 'preparing':
                return <MaterialIcons name="hourglass-top" size={16} color="#ea580c" />;
            case 'cancelled':
                return <MaterialIcons name="cancel" size={16} color="#dc2626" />;
            case 'closed':
                return <MaterialIcons name="done-all" size={16} color="#2563eb" />;
            case 'busy':
                return <MaterialIcons name="access-time" size={16} color="#d97706" />;
            case 'ready':
                return <MaterialIcons name="check-circle-outline" size={16} color="#16a34a" />;
            default:
                return <MaterialIcons name="help-outline" size={16} color="#6b7280" />;
        }
    }, []);

    const getPaymentTypeIcon = useCallback((type) => {
        switch (type) {
            case 'temporary':
                return <MaterialIcons name="pending" size={18} color="#6b7280" />;
            case 'cash':
                return <MaterialIcons name="payments" size={18} color="#4CAF50" />;
            case 'creditCard':
                return <MaterialIcons name="credit-card" size={18} color="#2196F3" />;
            case 'szepCard':
            case 'szepCreditCard':
                return <MaterialIcons name="card-membership" size={18} color="#9C27B0" />;
            case 'voucher':
                return <MaterialIcons name="local-offer" size={18} color="#FF9800" />;
            case 'employeeBalance':
                return <MaterialIcons name="account-balance-wallet" size={18} color="#607D8B" />;
            case 'vipBalance':
                return <MaterialIcons name="card-membership" size={18} color="#9C27B0" />;
            default:
                return <MaterialIcons name="help-outline" size={18} color="#9E9E9E" />;
        }
    }, []);

    const formattedPreOrderTime = useMemo(() => {
        return order?.preOrder ? new Date(order.preOrder).toLocaleString() : '';
    }, [order?.preOrder]);

    const createdTime = useMemo(() => {
        if (!order?.createdAt) {
            return '';
        }

        return new Date(order.createdAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
    }, [order?.createdAt]);

    const createdDate = useMemo(() => {
        if (!order?.createdAt) {
            return null;
        }

        const parsedDate = new Date(order.createdAt);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }, [order?.createdAt]);

    const totalItems = useMemo(() => {
        return (order?.items || []).reduce((sum, item) => sum + (item?.amount || item?.quantity || 0), 0);
    }, [order?.items]);

    const pairingStatus = useMemo(() => {
        const stats = getOrderPairingStats(order);

        if (!stats.requiresPairing || stats.totalPairableItems === 0) {
            return null;
        }

        return stats;
    }, [order]);

    const riskInfo = useMemo(() => {
        const risk = getOrderRisk(order);
        if (!risk || !risk.needsAttention) return null;
        const label = risk.discount && risk.discount.isHigh
            ? `${risk.discount.percent || 0}%`
            : String(risk.fraud?.score ?? risk.score ?? 0);
        return {label};
    }, [order]);

    const highlightStyle = useMemo(() => {
        if (!pairingStatus?.hasUnpairedItems || order?.storno || order?.status === 'cancelled' || order?.status === 'closed') {
            return null;
        }

        return styles.unpairedAttention;
    }, [order?.status, order?.storno, pairingStatus?.hasUnpairedItems]);

    const customerName = useMemo(() => {
        const firstName = order?.userInfo?.name?.first || '';
        const lastName = order?.userInfo?.name?.last || '';
        return `${firstName} ${lastName}`.trim();
    }, [order?.userInfo?.name?.first, order?.userInfo?.name?.last]);

    const customerPhone = order?.userInfo?.phone || order?.userInfo?.mobilePhone || order?.userInfo?.phoneNumber || '';
    const addressLine = order?.addressInfo
        ? `${order?.addressInfo?.zip || ''} ${order?.addressInfo?.city || ''}, ${order?.addressInfo?.address || ''}`.trim()
        : '';
    const tableName = typeof order?.tableName === 'string' ? order.tableName.trim() : '';
    const hasTableName = Boolean(tableName) && tableName.toLowerCase() !== 'null' && tableName.toLowerCase() !== 'undefined';

    const sourceLabel = order?.orderFrom === order?.restaurantPlace ? t('terminal') : (t(order?.orderFrom) || order?.orderFrom || t('terminal'));
    const statusLabel = t(order?.status) || order?.status;
    const orderTypeLabel = order?.orderType ? (t(order?.orderType) || order?.orderType) : t('temporary');

    const summaryItems = useMemo(() => {
        const items = [];

        if (createdDate) {
            items.push({
                key: 'time',
                label: t('order_row_time'),
                icon: <MaterialIcons name="access-time" size={15} color="#6b7280" />,
                value: <Text style={styles.summaryCellValue}><TimeAgo time={createdDate} interval={10} /> • {createdTime}</Text>,
            });
        }

        items.push({
            key: 'source',
            label: t('order_row_source'),
            icon: getOrderIcon(order?.orderFrom),
            value: sourceLabel,
        });

        items.push({
            key: 'type',
            label: t('order_row_type'),
            icon: getOrderTypeIcon(order?.orderType),
            value: orderTypeLabel,
        });

        if (hasTableName) {
            items.push({
                key: 'table',
                label: t('order_row_table'),
                icon: <MaterialIcons name="table-bar" size={15} color="#6b7280" />,
                value: tableName,
            });
        }

        if (order?.preOrder) {
            items.push({
                key: 'preorder',
                label: t('order_row_preorder'),
                icon: <MaterialIcons name="event" size={15} color="#b45309" />,
                value: formattedPreOrderTime,
                tone: 'warning',
            });
        }

        return items;
    }, [createdDate, createdTime, formattedPreOrderTime, getOrderIcon, getOrderTypeIcon, hasTableName, order?.orderFrom, order?.orderType, order?.preOrder, orderTypeLabel, sourceLabel, tableName, t]);

    const densePrimaryMeta = useMemo(() => {
        return [
            createdTime,
            sourceLabel,
            orderTypeLabel,
            hasTableName ? tableName : null,
        ].filter(Boolean).join(' • ');
    }, [createdTime, hasTableName, orderTypeLabel, sourceLabel, tableName]);

    const denseSecondaryMeta = useMemo(() => {
        if (order?.preOrder && formattedPreOrderTime) {
            return `${t('order_row_preorder')}: ${formattedPreOrderTime}`;
        }

        return null;
    }, [formattedPreOrderTime, order?.preOrder, t]);

    const handlePress = useCallback(() => {
        haptics.select();
        resetOnlineOrder();
        onPress?.(order);
    }, [onPress, order, resetOnlineOrder]);

    return (
        <TouchableOpacity
            onPress={handlePress}
            activeOpacity={0.9}
            style={[styles.orderCard, dense && styles.orderCardDense, cardToneStyle, highlightStyle]}
        >
            <View style={[styles.headerRow, isCompact && styles.headerRowCompact]}>
                <View style={styles.identityBlock}>
                    <View style={[styles.codeRow, isCompact && styles.codeRowCompact, dense && styles.codeRowDense]}>
                        <Text style={[styles.orderCode, dense && styles.orderCodeDense]}>#{order?.orderCode}</Text>
                        <View style={styles.statusBadge}>
                            {getStatusIcon(order?.status)}
                            <Text style={styles.statusText} numberOfLines={1}>{statusLabel}</Text>
                        </View>
                        {pairingStatus && (
                            <View style={[
                                styles.pairingBadge,
                                pairingStatus.allPaired ? styles.pairingBadgeSuccess : styles.pairingBadgePending,
                            ]}>
                                <MaterialIcons
                                    name={pairingStatus.allPaired ? 'check-circle' : 'link-off'}
                                    size={14}
                                    color={pairingStatus.allPaired ? '#166534' : '#991b1b'}
                                />
                                <Text style={[
                                    styles.pairingText,
                                    pairingStatus.allPaired ? styles.pairingTextSuccess : styles.pairingTextPending,
                                ]}>
                                    {pairingStatus.pairedItems}/{pairingStatus.totalPairableItems}
                                </Text>
                            </View>
                        )}
                        {riskInfo && (
                            <View style={styles.riskBadge}>
                                <MaterialIcons name="warning" size={14} color="#dc2626" />
                                <Text style={styles.riskBadgeText}>{riskInfo.label}</Text>
                            </View>
                        )}
                    </View>

                    {dense ? (
                        <View style={styles.denseMetaBlock}>
                            {densePrimaryMeta ? (
                                <Text style={styles.denseMetaText} numberOfLines={1}>
                                    {densePrimaryMeta}
                                </Text>
                            ) : null}
                            {denseSecondaryMeta ? (
                                <Text style={styles.denseMetaSecondaryText} numberOfLines={1}>
                                    {denseSecondaryMeta}
                                </Text>
                            ) : null}
                        </View>
                    ) : (
                        <View style={[styles.summaryGrid, isCompact && styles.summaryGridCompact]}>
                            {summaryItems.map((item) => (
                                <View
                                    key={item.key}
                                    style={[
                                        styles.summaryCell,
                                        item.tone === 'warning' && styles.summaryCellWarning,
                                        isCompact && styles.summaryCellCompact,
                                    ]}
                                >
                                    <View style={styles.summaryCellLabelRow}>
                                        {item.icon}
                                        <Text style={[
                                            styles.summaryCellLabel,
                                            item.tone === 'warning' && styles.summaryCellLabelWarning,
                                        ]}>
                                            {item.label}
                                        </Text>
                                    </View>
                                    {React.isValidElement(item.value) ? item.value : (
                                        <Text
                                            style={[
                                                styles.summaryCellValue,
                                                item.tone === 'warning' && styles.summaryCellValueWarning,
                                            ]}
                                            numberOfLines={item.tone === 'warning' ? 2 : 1}
                                        >
                                            {item.value}
                                        </Text>
                                    )}
                                </View>
                            ))}
                        </View>
                    )}
                </View>

                <View style={[styles.amountBlock, isCompact && styles.amountBlockCompact, dense && styles.amountBlockDense]}>
                    <View style={[styles.amountPanel, isCompact && styles.amountPanelCompact, dense && styles.amountPanelDense]}>
                        <View style={styles.paymentBadge}>
                            {getPaymentTypeIcon(order?.paymentType)}
                            <Text style={[styles.paymentText, dense && styles.paymentTextDense]}>{convertPaymentType(order?.paymentType)}</Text>
                        </View>
                        <Text style={[styles.orderAmount, isCompact && styles.orderAmountCompact, dense && styles.orderAmountDense]}>{priceFormatter(order?.orderSum || 0)}</Text>
                        <View style={styles.itemCountRow}>
                            <MaterialIcons name="shopping-cart" size={15} color="#6b7280" />
                            <Text style={styles.itemCountText}>{totalItems} {t('item-s')}</Text>
                        </View>
                    </View>
                </View>
            </View>

            {!dense && (customerName || customerPhone || addressLine) ? (
                <View style={styles.customerSection}>
                    {(customerName || customerPhone) ? (
                        <View style={styles.customerRow}>
                            <View style={styles.customerPrimary}>
                                <MaterialIcons name="person" size={16} color="#6b7280" />
                                <Text numberOfLines={1} style={styles.customerText}>{customerName || t('buyer_info')}</Text>
                            </View>
                            {customerPhone ? (
                                <View style={styles.customerSecondary}>
                                    <MaterialIcons name="phone" size={14} color="#6b7280" />
                                    <Text style={styles.customerSecondaryText}>{customerPhone}</Text>
                                </View>
                            ) : null}
                        </View>
                    ) : null}
                    {addressLine ? (
                        <View style={styles.customerRow}>
                            <View style={styles.customerPrimary}>
                                <MaterialIcons name="location-on" size={16} color="#6b7280" />
                                <Text numberOfLines={2} style={styles.customerText}>{addressLine}</Text>
                            </View>
                        </View>
                    ) : null}
                </View>
            ) : null}
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    orderCard: {
        width: '100%',
        borderRadius: 22,
        padding: 15,
        marginVertical: 7,
        minHeight: 168,
        borderWidth: 1,
        borderColor: 'rgba(148, 163, 184, 0.22)',
        shadowColor: '#0f172a',
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.08,
        shadowRadius: 16,
        elevation: 3,
    },
    orderCardDense: {
        borderRadius: 18,
        padding: 12,
        minHeight: 0,
        marginVertical: 5,
    },
    defaultBackground: {
        backgroundColor: 'rgba(255,255,255,0.96)',
    },
    stornoBackground: {
        backgroundColor: 'rgba(254, 242, 242, 0.98)',
        borderColor: 'rgba(248, 113, 113, 0.25)',
    },
    closedBackground: {
        backgroundColor: 'rgba(240, 253, 244, 0.98)',
        borderColor: 'rgba(74, 222, 128, 0.2)',
    },
    preOrderBackground: {
        backgroundColor: 'rgba(255, 251, 235, 0.98)',
        borderColor: 'rgba(245, 158, 11, 0.18)',
    },
    unpairedAttention: {
        borderColor: '#F39C12',
        borderWidth: 1.5,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 14,
    },
    headerRowCompact: {
        flexDirection: 'column',
    },
    identityBlock: {
        flex: 1,
        minWidth: 0,
    },
    codeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 12,
        minWidth: 0,
    },
    codeRowCompact: {
        marginBottom: 10,
    },
    codeRowDense: {
        marginBottom: 8,
    },
    orderCode: {
        fontSize: 24,
        fontWeight: '800',
        color: '#1f2937',
    },
    orderCodeDense: {
        fontSize: 18,
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#f3f4f6',
        borderRadius: 999,
        paddingHorizontal: 10,
        paddingVertical: 6,
        flexShrink: 1,
        minWidth: 0,
    },
    statusText: {
        marginLeft: 6,
        fontSize: 14,
        fontWeight: '700',
        color: '#4b5563',
        flexShrink: 1,
    },
    pairingBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 999,
        paddingHorizontal: 10,
        paddingVertical: 6,
    },
    pairingBadgeSuccess: {
        backgroundColor: '#DCFCE7',
    },
    pairingBadgePending: {
        backgroundColor: '#FEE2E2',
    },
    pairingText: {
        marginLeft: 5,
        fontSize: 12,
        fontWeight: '800',
    },
    pairingTextSuccess: {
        color: '#166534',
    },
    pairingTextPending: {
        color: '#991b1b',
    },
    riskBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 999,
        paddingHorizontal: 10,
        paddingVertical: 6,
        backgroundColor: '#FEE2E2',
    },
    riskBadgeText: {
        marginLeft: 5,
        fontSize: 12,
        fontWeight: '800',
        color: '#dc2626',
    },
    summaryGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    denseMetaBlock: {
        marginTop: 2,
    },
    denseMetaText: {
        fontSize: 12,
        lineHeight: 16,
        color: '#4b5563',
        fontWeight: '600',
    },
    denseMetaSecondaryText: {
        marginTop: 3,
        fontSize: 12,
        lineHeight: 16,
        color: '#9a3412',
        fontWeight: '600',
    },
    summaryGridCompact: {
        marginTop: 2,
    },
    summaryCell: {
        flexGrow: 1,
        flexBasis: '31%',
        minWidth: 118,
        backgroundColor: 'rgba(243,244,246,0.9)',
        borderRadius: 16,
        paddingHorizontal: 10,
        paddingVertical: 10,
    },
    summaryCellCompact: {
        flexBasis: '47%',
        minWidth: 0,
    },
    summaryCellWarning: {
        backgroundColor: '#FEF3C7',
    },
    summaryCellLabelRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    summaryCellLabel: {
        marginLeft: 6,
        fontSize: 11,
        fontWeight: '700',
        letterSpacing: 0.3,
        color: '#6b7280',
        textTransform: 'uppercase',
    },
    summaryCellLabelWarning: {
        color: '#b45309',
    },
    summaryCellValue: {
        marginTop: 6,
        fontSize: 13,
        lineHeight: 18,
        color: '#374151',
        fontWeight: '700',
    },
    summaryCellValueWarning: {
        color: '#92400E',
    },
    amountBlock: {
        minWidth: 150,
        alignItems: 'flex-end',
    },
    amountBlockCompact: {
        width: '100%',
        alignItems: 'flex-start',
    },
    amountBlockDense: {
        minWidth: 0,
        flexShrink: 1,
    },
    amountPanel: {
        minWidth: 156,
        alignItems: 'flex-end',
        backgroundColor: 'rgba(249,250,251,0.92)',
        borderRadius: 18,
        paddingHorizontal: 12,
        paddingVertical: 12,
    },
    amountPanelCompact: {
        width: '100%',
        alignItems: 'flex-start',
    },
    amountPanelDense: {
        minWidth: 0,
        alignSelf: 'stretch',
        borderRadius: 14,
        paddingHorizontal: 10,
        paddingVertical: 10,
    },
    paymentBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 8,
    },
    paymentText: {
        marginLeft: 6,
        fontSize: 14,
        fontWeight: '800',
        color: '#4b5563',
    },
    paymentTextDense: {
        fontSize: 12,
    },
    orderAmount: {
        fontSize: 18,
        fontWeight: '800',
        color: '#1f2937',
        textAlign: 'right',
    },
    orderAmountDense: {
        fontSize: 16,
    },
    orderAmountCompact: {
        textAlign: 'left',
    },
    itemCountRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginTop: 6,
    },
    itemCountText: {
        marginLeft: 6,
        fontSize: 13,
        color: '#6b7280',
        fontWeight: '600',
    },
    customerSection: {
        marginTop: 14,
        paddingTop: 14,
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: '#e5e7eb',
    },
    customerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 10,
        marginBottom: 8,
        flexWrap: 'wrap',
    },
    customerPrimary: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        flex: 1,
        minWidth: 0,
    },
    customerSecondary: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(243,244,246,0.8)',
        borderRadius: 999,
        paddingHorizontal: 10,
        paddingVertical: 5,
    },
    customerText: {
        marginLeft: 6,
        flex: 1,
        fontSize: 13,
        lineHeight: 18,
        color: '#374151',
        fontWeight: '600',
    },
    customerSecondaryText: {
        marginLeft: 5,
        fontSize: 12,
        color: '#4b5563',
        fontWeight: '700',
    },
});

export default React.memo(OrderRow);
